import { useState } from "react";
import axios from "axios";
import { z } from "zod";
import { getData } from "@/lib/utils";
import { useDispatch } from "react-redux";
import { getMyCompany, updateAuthUser } from "@/store/authSlice";
import api from "@/store/api";
import { useTranslate } from "@/locales";
import { useLocale } from "@/middleware";

const useLogin = () => {
  const { locale } = useLocale();
  const { t } = useTranslate(locale);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const dispatch = useDispatch();
  const LoginSchema = z.object({
    email: z
      .string()
      .email({ message: "authentication.validation.invalidEmail" }),
    password: z.string().min(8, {
      message: "authentication.changePassword.validation.passwordLength",
    }),
  });
  const refreshToken = async () => {
    let Token = getData("tokens")?.refreshToken;
    setIsLoading(true);
    setError(null);
    try {
      const response: { data: any; isSuccess: boolean } = await api.post(
        "/web/auth/refresh",
        {
          refreshToken: Token,
        }
      );
      setIsLoading(false);
      if (!response.isSuccess) {
        throw Error("not working");
      } else {
        return response.data;
      }
      // dispatch(updateAuthUser(response.data.data.user));
    } catch (err) {
      setIsLoading(false);
      dispatch(updateAuthUser(null));
      dispatch(getMyCompany(null));
      localStorage.removeItem("tokens");
      localStorage.removeItem("userInfo");
      window.location.reload();
      if (axios.isAxiosError(err)) {
        setError(err.response?.data?.message || t("alerts.error.loginError"));
        console.error("Axios Error:", err.message);
      } else {
        setError(t("alerts.error.unexpectedError"));
        console.error("Unexpected Error:", err);
      }

      throw err;
    }
  };
  const login = async (values: z.infer<typeof LoginSchema>) => {
    setIsLoading(true);
    setError(null);
    try {
      const response = await api.post("/web/auth/login", values);
      setIsLoading(false);
      dispatch(updateAuthUser(response.data.data.user));
      return response.data;
    } catch (err) {
      setIsLoading(false);

      if (axios.isAxiosError(err)) {
        setError(err.response?.data?.message || t("alerts.error.loginError"));
        console.error("Axios Error:", err.message);
      } else {
        setError(t("alerts.error.unexpectedError"));
        console.error("Unexpected Error:", err);
      }

      throw err;
    }
  };

  const getMe = async () => {
    setIsLoading(true);
    setError(null);

    try {
      let Token = getData("tokens")?.accessToken;
      const response = await api.get("/web/auth/me", {
        headers: {
          Authorization: `Bearer ${Token}`,
        },
      });
      setIsLoading(false);
      dispatch(updateAuthUser(response.data.data.user));
      return response.data;
    } catch (err) {
      setIsLoading(false);

      if (axios.isAxiosError(err)) {
        setError(
          err.response?.data?.message || t("alerts.error.userDataError")
        );
        console.error("Axios Error:", err.message);
      } else {
        // Handle other types of errors
        setError(t("alerts.error.unexpectedError"));
        console.error("Unexpected Error:", err);
      }

      throw err;
    }
  };

  return { login, refreshToken, getMe, isLoading, error };
};

export default useLogin;
